<template>
  <div
    ref="banner"
    class="sticky !transition-none top-0 bg-secondary flex justify-between items-center container-padding h-14 z-[1] text-white"
  >
    <h1 class="text-lg font-semibold mb-0 text-white">
      {{ currentPageTitle }}
    </h1>

    <div
      v-if="showNavigation"
      class="flex items-center gap-3"
    >
      <template v-if="user.roles.includes(Role.Admin) || user.roles.includes(Role.Capturer)">
        <!--MOBILE-->
        <PlanMenuDropdown :nav-items="navItems" />

        <!--DESKTOP-->
        <div class="lg:flex flex-wrap gap-3 hidden">
          <template
            v-for="(item, index) in navItems"
            :key="index"
          >
            <CatalyticProjectsButton v-if="item.isCatalytic" />

            <ButtonBannerPlan
              v-else
              :to="{ name: item.route }"
              :target="item.target"
              :text="item.label"
            />
          </template>
        </div>
      </template>

      <AppButton
        v-if="user.roles.includes(Role.Admin) || user.roles.includes(Role.Capturer)"
        type="button"
        class="btn bg-white text-secondary w-24 !rounded-none !border-0 !outline-0
        hover:bg-[#FFFFFF29] hover:text-white disabled:pointer-events-none"
        :processing="planStore.saving"
        :disabled="planStore.loading"
        @click="$emit('save')"
      >
        <span class="text-nowrap">{{ planStore.saving ? 'Saving' : 'Save' }}</span>
      </AppButton>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed } from 'vue';
import { AppButton, ButtonBannerPlan, CatalyticProjectsButton } from '@/components/buttons';
import { useAuthStore, usePlanStore } from '@/store/index.js';
import { PlanMenuDropdown } from '@/components/dropdowns/index.js';
import { Role } from '@/const.js';
import { useRoute } from 'vue-router';

const route = useRoute();
const planStore = usePlanStore();
const user = useAuthStore().user;

defineEmits(['save']);

defineProps({
  showNavigation: {
    type: Boolean,
    default: true,
  },
});

const banner = ref(null);
const isStuck = ref(false);

const navItems = [
  { label: 'Coverpage', route: 'app_plan_cover_page' },
  { label: 'Introduction', route: 'app_plan_introduction' },
  { label: 'Diagnostics', route: 'app_plan_diagnostics' },
  { label: 'Vision Setting', route: 'app_plan_vision' },
  { isCatalytic: true },
  { label: 'Conclusion', route: 'app_plan_conclusion' },
  { label: 'Plan Preview', route: 'app_plan_pdf_preview', target: '_blank' },
];

const handleIntersection = ([entry]) => {
  // Set `isStuck` to true when the element is NOT intersecting
  isStuck.value = !entry.isIntersecting;
};

let observer;

onMounted(() => {
  observer = new IntersectionObserver(handleIntersection, {
    threshold: 1.0, // Ensure full visibility before considering it unstuck
    rootMargin: '-1px 0px 0px 0px', // This helps with detecting the top intersection
  });

  if (banner.value) {
    observer.observe(banner.value);
  }
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});

const currentPageTitle = computed(() => {
  if (route.meta && route.meta.title) return route.meta.title;

  return route.name
    ?.replace('app_plan_', '')
    ?.replace(/_/g, ' ')
    ?.replace(/\b\w/g, l => l.toUpperCase()) || 'Plan Section';
});
</script>
